import { ApplicationConfig, provideZoneChangeDetection, inject, provideAppInitializer } from '@angular/core';
import { provideRouter, withComponentInputBinding, withInMemoryScrolling } from '@angular/router';
import { provideClientHydration, withEventReplay, withHttpTransferCacheOptions } from '@angular/platform-browser';
import { provideServiceWorker } from '@angular/service-worker';
import { initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { getMessaging, provideMessaging } from '@angular/fire/messaging';
import { HttpClient, withFetch, withInterceptors } from '@angular/common/http';
import { provideHttpClient } from "@angular/common/http";
import { MissingTranslationHandler, provideTranslateService, TranslateParser } from "@ngx-translate/core";
import { TranslateLoader } from "@ngx-translate/core";
import { routes } from './app.routes';
import { GeneralService } from '@core/services/general.service';
import { DomainService } from '@core/services/domain.service';
import { apiInterceptor } from '@core/interceptor/api.interceptor';
import { withdrawErrorInterceptor } from '@core/interceptor/withdraw-error.interceptor';
import { environment } from '@env/environment';
import { provideStore } from '@ngrx/store';
import { provideEffects } from '@ngrx/effects';
import { authReducer } from '@core/entity/auth/state/auth.reducers';
import { AuthEffects } from '@core/entity/auth/state/auth.effects';
import { memberProfileReducer } from '@core/entity/member-profile/state/member-profile.reducers';
import { legacySessionKeys } from '@core/entity/auth/state/legacy-session-keys.meta-reducer';
import { MemberProfileEffects } from '@core/entity/member-profile/state/member-profile.effects';
import { SEOHttpService } from '@core/entity/seo/service/seo.http.service';
import { PlatformType } from '@shared/types/platform.type';
import { IpAddressHttpService } from '@core/entity/ip-address/service/ip-address.http.service';
import { MultiTranslateHttpLoader } from '@core/custom/translate/MultiTranslateHttpLoader';
import { CustomMissingTranslationHandler } from '@core/custom/translate/CustomMissingTranslationHandler';
import { MaintenanceHttpService } from '@core/services/maintenance-http.service';
import { CustomParser } from '@core/custom/translate/CustomParser';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { PlatformService } from '@core/services/platform.service';
import { DatePipe } from '@angular/common';

/* Disable console debug in production */
if (environment.production) {
  console.debug = function (): void { };
}

/* Start : NGX Translate */
/* translationDomain includes the scheme (e.g. https://qa-api2.jk188.net); strip it so the
   loader builds a protocol-relative `//host/i18n/...` URL (apiInterceptor prepends `https:`).
   Otherwise the prefix is `//https://host/...` → the broken `https://https//host/...` URL. */
const translationHost: string = environment.translationDomain.replace(/^https?:\/\//, '').replace(/\/+$/, '');
const httpLoaderFactory: (http: HttpClient) => MultiTranslateHttpLoader = (http: HttpClient) =>
  new MultiTranslateHttpLoader(http, [
    { prefix: `//${translationHost}/i18n/`, suffix: '.json', fileName: 'message' },
    { prefix: `//${translationHost}/i18n/`, suffix: '.json', fileName: 'string' },
    { prefix: `//${translationHost}/i18n/`, suffix: '.json', fileName: 'validation' },
    { prefix: `//${translationHost}/i18n/`, suffix: '.json', fileName: 'vip-tier' }
  ]);
/* End : NGX Translate */

/* Start : APP_INITIALIZER function */
export function appInitializer(platformService: PlatformService, generalService: GeneralService, domainService: DomainService, seoHttpService: SEOHttpService, ipAddressHttpService: IpAddressHttpService, maintenanceService: MaintenanceHttpService) {
  const
    platform: PlatformType = platformService.isServer() ? 'server' : 'browser';

  return (): Promise<any> => {
    return new Promise<void>(async (resolve, reject) => {
      /* Do not change the order */
      console.debug(`--- Start: appInitializer ---\n\n`);
      await domainService.loadDynamicDomainConfig(platform);
      await generalService.setDeviceType(platform);
      await generalService.setSessionId(platform);
      await generalService.setClientBrowserLanguage(platform);
      await generalService.setUuidFingerprint(platform);
      if (await maintenanceService.checkMaintenance(platform)) {
        await domainService.setDomainAndPath(platform);

        if (await domainService.checkIsNonRegionalDomain(platform, ipAddressHttpService) && await domainService.checkDomainAndLocale(platform)) {
          if (platform == 'browser') {
            await domainService.processQueryParams(platform);
            await domainService.checkMode(platform);
            await seoHttpService.gTagInit(platform);
          }

          if (platform == 'server') {
            if (await domainService.checkDomainWithDeviceType(platform)) {
              await seoHttpService.setJsonLd(platform);
              await seoHttpService.seoInit(platform)
            }
          };
        }
      }

      console.debug(`--- End: appInitializer ---\n\n`);
      resolve();
    });
  };
}
/* End : APP_INITIALIZER function */

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes, withComponentInputBinding(), withInMemoryScrolling({ scrollPositionRestoration: 'top' })),
    provideClientHydration(
      withEventReplay(),
      withHttpTransferCacheOptions({ filter: (req) => !req.url.includes('/api/member/') })
    ),
    provideServiceWorker('ngsw-worker.js', {
      enabled: environment.production,
      registrationStrategy: 'registerWhenStable:30000'
    }),
    provideHttpClient(withFetch(), withInterceptors([withdrawErrorInterceptor, apiInterceptor])),
    provideTranslateService({
      fallbackLang: 'en',
      loader: {
        provide: TranslateLoader,
        useFactory: httpLoaderFactory,
        deps: [HttpClient],
      },
      parser: { provide: TranslateParser, useClass: CustomParser },
      missingTranslationHandler: { provide: MissingTranslationHandler, useClass: CustomMissingTranslationHandler },
    }),
    provideStore({
      'AuthState': authReducer,
      'MemberProfileState': memberProfileReducer
    }, { metaReducers: [legacySessionKeys] }),
    provideEffects([MemberProfileEffects, AuthEffects]),
    provideAppInitializer(() => {
      const initializerFn = (appInitializer)(inject(PlatformService), inject(GeneralService), inject(DomainService), inject(SEOHttpService), inject(IpAddressHttpService), inject(MaintenanceHttpService));
      return initializerFn();
    }),
    provideAnimationsAsync(),
    provideFirebaseApp(() => initializeApp(environment.firebaseConfig)),
    /* to prevent app crashing in environment like localhost dev testing where SSL is absent */
    provideMessaging(() => {
      try {
        return getMessaging();
      } catch (error) {
        console.error('Could not initialize messaging');
        return null;
      }
    }),
    DatePipe,
  ]
};